import { useMemo } from 'react';
import { useCustom, useGo } from '@refinedev/core';
import {
  AlertTriangle,
  BookOpen,
  Boxes,
  Calendar,
  CheckCircle2,
  Clock,
  File as FileIcon,
  FileText,
  FolderOpen,
  Hash,
  Headphones,
  Image as ImageIcon,
  Languages,
  Loader2,
} from 'lucide-react';
import { BookFormat, BookProcessingStatus } from '@masters-club/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  EntityAnalyticsCard,
  EntityAnalyticsGrid,
  TwoColumnLayout,
} from '@/components/shared';
import { resolveApiUrl } from '@/providers/api-url';
import { getBookTitle, getChapterTitle, type Book, type BookTranslationData } from '../types';
import { BookAnalyticsSection } from './BookAnalyticsTab';

const API_URL = resolveApiUrl();

interface PricingTierRow {
  id: number;
  name: string | null;
  bookId: number | null;
  isActive: boolean;
}

const FORMAT_LABEL: Record<BookFormat, string> = {
  [BookFormat.EPUB]: 'EPUB',
  [BookFormat.PDF]: 'PDF',
  [BookFormat.FB2]: 'FB2',
  [BookFormat.AUDIO]: 'Аудиокнига',
};

const PROCESSING_LABEL: Record<BookProcessingStatus, string> = {
  [BookProcessingStatus.PENDING]: 'В очереди',
  [BookProcessingStatus.PROCESSING]: 'Обработка',
  [BookProcessingStatus.READY]: 'Готова',
  [BookProcessingStatus.ERROR]: 'Ошибка',
};

function formatDuration(sec: number | null): string {
  if (sec === null) { return '\u2014'; }
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) { return '\u2014'; }
  return new Date(value).toLocaleString('ru-RU');
}

function truncate(text: string, max = 80): string {
  if (text.length <= max) { return text; }
  return `${text.slice(0, max)}…`;
}

function getFormatIcon(format: BookFormat): React.ReactNode {
  if (format === BookFormat.AUDIO) { return <Headphones className="h-4 w-4" />; }
  if (format === BookFormat.PDF) { return <FileText className="h-4 w-4" />; }
  return <BookOpen className="h-4 w-4" />;
}

function getProcessingIcon(status: BookProcessingStatus): React.ReactNode {
  if (status === BookProcessingStatus.READY) {
    return <CheckCircle2 className="h-4 w-4 text-green-600" />;
  }
  if (status === BookProcessingStatus.ERROR) {
    return <AlertTriangle className="h-4 w-4 text-red-600" />;
  }
  if (status === BookProcessingStatus.PROCESSING) {
    return <Loader2 className="h-4 w-4 animate-spin text-blue-600" />;
  }
  return <Clock className="h-4 w-4 text-muted-foreground" />;
}

function getPublishBadge(isPublished: boolean): React.ReactNode {
  return isPublished ? (
    <Badge className="bg-green-500/10 text-green-600 border border-green-500/20">
      Опубликована
    </Badge>
  ) : (
    <Badge variant="secondary">Черновик</Badge>
  );
}

function getTranslationCompleteness(t: BookTranslationData): {
  title: boolean;
  description: boolean;
  author: boolean;
} {
  return {
    title: Boolean(t.title && t.title.trim().length > 0),
    description: Boolean(t.description && t.description.trim().length > 0),
    author: Boolean(t.authorDisplay && t.authorDisplay.trim().length > 0),
  };
}

interface CompletenessMarkProps {
  filled: boolean;
  label: string;
}

function CompletenessMark({ filled, label }: CompletenessMarkProps) {
  return (
    <span
      className={
        filled
          ? 'inline-flex items-center gap-1 text-xs text-green-600'
          : 'inline-flex items-center gap-1 text-xs text-muted-foreground'
      }
    >
      {filled ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
      {label}
    </span>
  );
}

interface BookOverviewTabProps {
  book: Book;
}

export function BookOverviewTab({ book }: BookOverviewTabProps) {
  const go = useGo();

  const { query: tiersQuery } = useCustom<PricingTierRow[]>({
    url: `${API_URL}/pricing-tiers/admin`,
    method: 'get',
    config: { query: { bookId: book.id } },
  });

  const tiers = tiersQuery.data?.data ?? [];
  const activeTiers = tiers.filter((t) => t.isActive);
  const isPaid = activeTiers.length > 0;
  const primaryTierName = activeTiers[0]?.name ?? null;

  const primaryTranslation = useMemo(() => {
    const list = book.translations ?? [];
    return (
      list.find((t) => t.languageCode === book.languageCode)
      ?? list.find((t) => t.languageCode === 'ru')
      ?? list[0]
      ?? null
    );
  }, [book.translations, book.languageCode]);

  const isAudio = book.format === BookFormat.AUDIO;
  const chaptersCount = book.chapters.length;
  const totalPages = book.totalPages;

  const sortedChapters = useMemo(
    () => [...book.chapters].sort((a, b) => a.order - b.order),
    [book.chapters],
  );

  const mainContent = (
    <div className="space-y-6">
      {/* Обложка и описание */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BookOpen className="h-4 w-4" />
            Обложка и описание
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="w-full sm:w-40 shrink-0">
              {book.coverFileUrl ? (
                <div className="overflow-hidden rounded-lg border">
                  <img
                    src={book.coverFileUrl}
                    alt=""
                    className="aspect-[2/3] w-full object-cover"
                  />
                </div>
              ) : (
                <div className="flex aspect-[2/3] w-full items-center justify-center rounded-lg border bg-muted">
                  <ImageIcon className="h-10 w-10 text-muted-foreground" />
                </div>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <h3 className="text-lg font-semibold leading-tight">{getBookTitle(book)}</h3>
              {(primaryTranslation?.authorDisplay || book.author) && (
                <p className="text-sm text-muted-foreground">
                  {primaryTranslation?.authorDisplay ?? book.author}
                </p>
              )}
              {primaryTranslation?.description ? (
                <p className="text-sm whitespace-pre-line">{primaryTranslation.description}</p>
              ) : (
                <p className="text-sm text-muted-foreground italic">Описание не заполнено</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Переводы */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Languages className="h-4 w-4" />
            Переводы ({book.translations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {book.translations.length === 0 ? (
            <p className="text-sm text-muted-foreground">Переводы не заполнены</p>
          ) : (
            <div className="space-y-2">
              {book.translations.map((t) => {
                const c = getTranslationCompleteness(t);
                return (
                  <div
                    key={t.languageCode}
                    className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{t.languageCode.toUpperCase()}</Badge>
                      <span className="font-medium">
                        {c.title ? t.title : <span className="text-muted-foreground italic">Не заполнено</span>}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CompletenessMark filled={c.title} label="Название" />
                      <CompletenessMark filled={c.description} label="Описание" />
                      <CompletenessMark filled={c.author} label="Автор" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Главы */}
      {chaptersCount > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Boxes className="h-4 w-4" />
              Главы ({chaptersCount})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-[420px] space-y-2 overflow-y-auto pr-1">
              {sortedChapters.slice(0, 10).map((c) => (
                <div
                  key={c.id ?? `order-${c.order}`}
                  className="flex items-center justify-between gap-3 rounded-md border p-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {c.order + 1}. {getChapterTitle(c)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {isAudio
                        ? formatDuration(c.durationSec)
                        : `Стр. ${c.pageStart ?? '\u2014'}\u2013${c.pageEnd ?? '\u2014'}`}
                    </p>
                  </div>
                </div>
              ))}
              {chaptersCount > 10 && (
                <p className="pt-1 text-xs text-muted-foreground">
                  и ещё {chaptersCount - 10}…
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <BookAnalyticsSection bookId={book.id} />
    </div>
  );

  const sidebarContent = (
    <div className="space-y-6">
      {/* Метаданные */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Hash className="h-4 w-4" />
            Метаданные
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div>
            <span className="text-muted-foreground">ID</span>
            <p className="font-mono font-medium">#{book.id}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Slug</span>
            <p className="font-mono font-medium break-all">{book.slug}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Основной язык</span>
            <p className="font-medium">{book.languageCode.toUpperCase()}</p>
          </div>
          <Separator />
          <div>
            <span className="flex items-center gap-1 text-muted-foreground">
              <Calendar className="h-3 w-3" /> Создана
            </span>
            <p className="font-medium">{formatDateTime(book.createdAt)}</p>
          </div>
          <div>
            <span className="flex items-center gap-1 text-muted-foreground">
              <Calendar className="h-3 w-3" /> Обновлена
            </span>
            <p className="font-medium">{formatDateTime(book.updatedAt)}</p>
          </div>
          {book.translationGroupId && (
            <>
              <Separator />
              <div>
                <span className="text-muted-foreground">Группа переводов</span>
                <p className="font-mono text-xs font-medium break-all">{book.translationGroupId}</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Доступ */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FolderOpen className="h-4 w-4" />
            Доступ
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <span className="text-muted-foreground">Тип</span>
            <div className="mt-1">
              {isPaid ? (
                <Badge variant="outline">Платная</Badge>
              ) : (
                <Badge className="bg-blue-500/10 text-blue-600 border border-blue-500/20">
                  Бесплатная
                </Badge>
              )}
            </div>
          </div>
          {isPaid && (
            <>
              <div>
                <span className="text-muted-foreground">Основной тариф</span>
                <p className="font-medium">{primaryTierName ?? '\u2014'}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Активных тарифов</span>
                <p className="font-medium">{activeTiers.length}</p>
              </div>
            </>
          )}
          <Separator />
          <Button
            variant="link"
            className="h-auto p-0"
            onClick={() => go({ to: `/books/edit/${book.id}`, query: { tab: 'pricing' } })}
          >
            Перейти к тарифам
          </Button>
        </CardContent>
      </Card>

      {/* Файлы */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileIcon className="h-4 w-4" />
            Файлы
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {!book.sourceFileUrl && !book.coverFileUrl ? (
            <div className="flex flex-col items-center gap-2 py-4 text-center">
              <FileIcon className="h-8 w-8 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">Файлы не прикреплены</p>
            </div>
          ) : (
            <>
              <div>
                <span className="text-muted-foreground">Исходник</span>
                {book.sourceFileUrl ? (
                  <p className="font-medium">
                    <a
                      href={book.sourceFileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      <FileText className="h-3 w-3" /> Открыть файл #{book.sourceFileId}
                    </a>
                  </p>
                ) : (
                  <p className="text-muted-foreground">{'\u2014'}</p>
                )}
              </div>
              <Separator />
              <div>
                <span className="text-muted-foreground">Обложка</span>
                {book.coverFileUrl ? (
                  <p className="font-medium">
                    <a
                      href={book.coverFileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      <ImageIcon className="h-3 w-3" />
                      {book.coverFileId ? `Открыть файл #${book.coverFileId}` : 'Открыть'}
                    </a>
                  </p>
                ) : (
                  <p className="text-muted-foreground">Не прикреплена</p>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const processingStatus = book.processingStatus;
  const processingDescription =
    processingStatus === BookProcessingStatus.ERROR && book.processingError
      ? truncate(book.processingError, 60)
      : PROCESSING_LABEL[processingStatus];

  return (
    <div className="space-y-6">
      <EntityAnalyticsGrid>
        <EntityAnalyticsCard
          title="Формат"
          value={FORMAT_LABEL[book.format]}
          icon={getFormatIcon(book.format)}
          description={`Язык: ${book.languageCode.toUpperCase()}`}
        />
        <EntityAnalyticsCard
          title="Статус"
          value={getPublishBadge(book.isPublished)}
          icon={book.isPublished ? (
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          ) : (
            <Clock className="h-4 w-4 text-muted-foreground" />
          )}
          description={book.publishedAt ? formatDateTime(book.publishedAt) : undefined}
        />
        <EntityAnalyticsCard
          title="Обработка"
          value={PROCESSING_LABEL[processingStatus]}
          icon={getProcessingIcon(processingStatus)}
          description={processingDescription}
        />
        {isAudio || chaptersCount > 0 ? (
          <EntityAnalyticsCard
            title="Главы"
            value={chaptersCount}
            icon={<Boxes className="h-4 w-4" />}
            description={isAudio ? formatDuration(book.totalDurationSec) : 'Всего глав'}
          />
        ) : (
          <EntityAnalyticsCard
            title="Страницы"
            value={totalPages ?? '\u2014'}
            icon={<FileText className="h-4 w-4" />}
            description="Страниц"
          />
        )}
      </EntityAnalyticsGrid>

      <TwoColumnLayout main={mainContent} sidebar={sidebarContent} />
    </div>
  );
}
